import {
  type ListOffersResponse,
  offerStateLabels,
} from '@borradh-workspace/api-client/types';

/** One promotion as the org's offer list returns it. */
export type PromotionOption = Pick<
  ListOffersResponse['items'][number],
  'id' | 'name' | 'state' | 'serviceIds'
>;

/** States a new ad can be linked to: live, or a draft published on launch. */
const PICKABLE_STATES: ReadonlySet<string> = new Set(['active', 'draft']);

export interface PromotionOptionGroups {
  /** Promotions linked to one of the ad's services. Listed first. */
  forService: PromotionOption[];
  /** Every other pickable promotion. */
  others: PromotionOption[];
}

/**
 * The promotions the ad wizard offers: live and draft ones, those linked to
 * the ad's service(s) first. The current selection is always kept, whatever
 * its state (a creative's paused promotion must not silently vanish from the
 * trigger). Live before draft within each group; otherwise the API's order.
 */
export function groupPromotionOptions(
  offers: readonly PromotionOption[],
  {
    serviceIds,
    selectedId,
  }: { serviceIds: readonly string[]; selectedId?: string }
): PromotionOptionGroups {
  const pickable = offers.filter(
    (o) => PICKABLE_STATES.has(o.state) || o.id === selectedId
  );
  const liveFirst = [...pickable].sort(
    (a, b) => Number(a.state !== 'active') - Number(b.state !== 'active')
  );
  const forService: PromotionOption[] = [];
  const others: PromotionOption[] = [];
  for (const offer of liveFirst) {
    const linked = offer.serviceIds.some((id) => serviceIds.includes(id));
    (linked ? forService : others).push(offer);
  }
  return { forService, others };
}

/** "Spring Balayage" for a live promotion, "Spring Balayage · Draft" otherwise. */
export function promotionOptionLabel(offer: PromotionOption): string {
  if (offer.state === 'active') return offer.name;
  const state =
    offerStateLabels[offer.state as keyof typeof offerStateLabels] ??
    offer.state;
  return `${offer.name} · ${state}`;
}

/** True when the picker holds a promotion (not untouched). */
export function hasPromotion(value: string | undefined): value is string {
  return Boolean(value);
}

/**
 * A price or a discount in ad copy: "£120", "€ 80", "$50", "120 euro",
 * "50% off", "20 % discount", "half price". Deliberately loose — it only
 * decides whether to show a non-blocking hint.
 */
const PRICE_PATTERN =
  /[£€$]\s?\d|\d(?:[.,]\d+)?\s?(?:€|£|eur(?:os?)?\b|gbp\b|usd\b|dollars?\b|pounds?\b|quid\b)|\d+\s?%\s?(?:off|discount)\b|\bhalf[-\s]?price\b/i;

export function copyMentionsPrice(
  ...texts: readonly (string | undefined | null)[]
): boolean {
  return texts.some((text) => Boolean(text) && PRICE_PATTERN.test(text ?? ''));
}

/**
 * What the promotion picker should hold after the ad's creative changes.
 *
 * A generated graphic/video carries the promotion it was made for; while the
 * owner has not touched the picker, it follows the creative. Explicit changes
 * stay selected even when the owner returns to the original suggestion.
 *
 * Only a promotion the org's list knows is preselected: an explicit id the API
 * cannot find is refused, whereas leaving the field untouched lets the server
 * inherit (or drop) the creative's promotion by itself.
 *
 * Returns `null` when nothing should change.
 */
export function nextPromotionForCreative(input: {
  current: string | undefined;
  /** Form state distinguishes an owner choice from an automatic suggestion. */
  ownerSelected?: boolean;
  /** The value this logic last preselected, or null. */
  preselected: string | null;
  creativeOfferId: string | null | undefined;
  knownOfferIds: ReadonlySet<string>;
}): { value: string; preselected: string | null } | null {
  const current = input.current ?? '';
  const touched = current !== '' && current !== input.preselected;
  if (input.ownerSelected || touched) return null;

  const creativeOfferId =
    input.creativeOfferId && input.knownOfferIds.has(input.creativeOfferId)
      ? input.creativeOfferId
      : null;
  const value = creativeOfferId ?? '';
  if (value === current && creativeOfferId === input.preselected) return null;
  return { value, preselected: creativeOfferId };
}
